/** @jsx jsx */
// @flow

import bemify, { toModifiers } from 'bemify-js';

import { GLOBAL_APP_COUNT } from '@zapier/constants';
import { jsx } from '@emotion/core';
import { labelStyles } from '@zapier/common-utils';
import { Colors, Icon, Typography } from '@zapier/design-system';

import SimplePopover, {
  SimplePopoverWrapper,
} from 'app/common/components/SimplePopover';
import 'app/login/components/FeatureItems.scss';

import { type Node } from 'react';

import type { KindType } from 'app/common/types';

const bem = bemify('feature-items');

type FeatureItemProps = {
  children: Node,
};

const Styles = labelStyles(__filename, {
  descriptionContainer: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    marginTop: 15,

    '&:first-of-type': {
      marginTop: 0,
    },
  },
  description: {
    ...Typography.paragraph3,
    color: Colors.gray,
    margin: '0 0 0 10px',
  },
});

const FeatureItem = (props: FeatureItemProps) => (
  <div css={Styles.descriptionContainer}>
    <Icon color="success" name="formCheckCircle" size={20} />
    <div css={Styles.description}>{props.children}</div>
  </div>
);

export const TeamContainerFeatureItems = (
  kind?: KindType,
  className?: string
) => (
  <div className={bem(toModifiers(kind), className)}>
    <FeatureItem>Help build and maintain this integration</FeatureItem>
    <FeatureItem>Access data on how your integration is performing</FeatureItem>
    <FeatureItem>Get a free Zapier trial</FeatureItem>
  </div>
);

export const PlatformContainerStartFeaturedItems = (
  kind?: KindType,
  className?: string
) => (
  <div className={bem(toModifiers(kind), className)}>
    <FeatureItem>Build and maintain your integration</FeatureItem>
    <FeatureItem>Access performance data on your integration</FeatureItem>
    <FeatureItem>Includes a free trial of Zapier</FeatureItem>
  </div>
);

export const PlatformContainerWhyFeaturedItems = (
  kind?: KindType,
  className?: string
) => (
  <div className={bem(toModifiers(kind), className)}>
    <FeatureItem>
      Build one integration that gives your users access to {GLOBAL_APP_COUNT}+
      different apps
    </FeatureItem>
    <FeatureItem>
      Additional frontline support for your integration, available 24 hours
    </FeatureItem>
    <FeatureItem>
      Support for your GTM launch strategy and ongoing co-marketing
      opportunities
    </FeatureItem>
    <FeatureItem>Access performance and health data</FeatureItem>
    <FeatureItem>Free to build, with no recurring charges</FeatureItem>
  </div>
);

type FeatureItemsProps = {
  kind?: KindType,
  className?: string,
};

const FeatureItems = (props: FeatureItemsProps) => (
  <div className={bem(toModifiers(props.kind), props.className)}>
    <FeatureItem>Easy setup, no coding required</FeatureItem>
    <FeatureItem>Free forever for core features</FeatureItem>
    <FeatureItem>
      14-day trial of&nbsp;
      <SimplePopoverWrapper className={bem('__popover')} isHoverable={true}>
        <span className={bem('__popover-hover')}>premium features</span>
        <SimplePopover direction="up" kind="compact no-wrap dark">
          <ul className={bem('__popover-content')}>
            <li>Zaps with 3+ steps</li>
            <li>Priority support</li>
          </ul>
        </SimplePopover>
      </SimplePopoverWrapper>
      &nbsp;&amp; apps
    </FeatureItem>
  </div>
);

export default FeatureItems;
